import { Suspense, useLayoutEffect, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import LayoutAdmin   from './components/layout/LayoutAdmin'
import LayoutCliente from './components/layout/LayoutCliente'
import ErrorBoundary from './components/ErrorBoundary'
import RouteContentFallback from './components/ui/RouteContentFallback'
import { beginRequestScope } from './utils/api'
import { lazyWithRetry } from './utils/lazyWithRetry'
import Login from './pages/Login'
const DashboardAdmin = lazyWithRetry(() => import('./pages/admin/Dashboard'))
const Clientes = lazyWithRetry(() => import('./pages/admin/Clientes'))
const DetalheCliente = lazyWithRetry(() => import('./pages/admin/DetalheCliente'))
const DetalheContrato = lazyWithRetry(() => import('./pages/admin/DetalheContrato'))
const Configuracoes = lazyWithRetry(() => import('./pages/admin/Configuracoes'))
const Financeiro = lazyWithRetry(() => import('./pages/admin/Financeiro'))
const Usuarios = lazyWithRetry(() => import('./pages/Usuarios'))
const Auditoria = lazyWithRetry(() => import('./pages/admin/Auditoria'))
const SaudeOperacional = lazyWithRetry(() => import('./pages/admin/SaudeOperacional'))

const DashboardTecnico = lazyWithRetry(() => import('./pages/tecnico/Dashboard'))
const Agenda = lazyWithRetry(() => import('./pages/tecnico/Agenda'))
const DashboardCliente = lazyWithRetry(() => import('./pages/cliente/Dashboard'))
const Chamados = lazyWithRetry(() => import('./pages/Chamados'))
const DetalheChamado = lazyWithRetry(() => import('./pages/DetalheChamado'))
const Fechamento = lazyWithRetry(() => import('./pages/Fechamento'))
const FaturaDetalhe = lazyWithRetry(() => import('./pages/FaturaDetalhe'))
const Perfil = lazyWithRetry(() => import('./pages/Perfil'))
const Hoje = lazyWithRetry(() => import('./pages/Hoje'))
const RotinasPreventivas = lazyWithRetry(() => import('./pages/RotinasPreventivas'))
const Ativos = lazyWithRetry(() => import('./pages/Ativos'))
const Conhecimento = lazyWithRetry(() => import('./pages/Conhecimento'))
const CrmBoard = lazyWithRetry(() => import('./pages/CRM/Board'))
const CrmAnalytics = lazyWithRetry(() => import('./pages/CRM/Analytics'))
const CrmProdutividade = lazyWithRetry(() => import('./pages/CRM/Produtividade'))
const CrmConfiguracoes = lazyWithRetry(() => import('./pages/CRM/Configuracoes'))
const CatalogoPrecificacao = lazyWithRetry(() => import('./pages/CRM/CatalogoPrecificacao'))
const CatalogoPlanos = lazyWithRetry(() => import('./pages/CRM/CatalogoPlanos'))
const CatalogoModulos = lazyWithRetry(() => import('./pages/CRM/CatalogoModulos'))
const EstoqueOperacao = lazyWithRetry(() => import('./pages/EstoqueOperacao'))
const EstoqueReservas = lazyWithRetry(() => import('./pages/EstoqueReservas'))
const EstoqueInventarios = lazyWithRetry(() => import('./pages/EstoqueInventarios'))
const CrmDetalhe = lazyWithRetry(() => import('./pages/CRM/OportunidadeDetalhe'))
const OrcamentoPublico = lazyWithRetry(() => import('./pages/CRM/OrcamentoPublico'))
const RouteState = lazyWithRetry(() => import('./pages/RouteState'))
const AceitarConvite = lazyWithRetry(() => import('./pages/AceitarConvite'))
const AlteracaoSenhaObrigatoria = lazyWithRetry(() => import('./pages/AlteracaoSenhaObrigatoria'))
const BancoSenhas = lazyWithRetry(() => import('./pages/BancoSenhas'))
const Pessoas = lazyWithRetry(() => import('./pages/Pessoas'))
const GestaoPonto = lazyWithRetry(() => import('./pages/GestaoPonto'))
const CentralAjuda = lazyWithRetry(() => import('./pages/cliente/CentralAjuda'))
const AtendimentoWhatsApp = lazyWithRetry(() => import('./pages/admin/AtendimentoWhatsApp'))
const ChatwootDashboardApp = lazyWithRetry(() => import('./pages/chatwoot/ChatwootDashboardApp'))
const DiretoriaIA = lazyWithRetry(() => import('./pages/admin/DiretoriaIA'))
const PoliticaPrivacidade = lazyWithRetry(() => import('./pages/public/PoliticaPrivacidade'))
const TermosServico = lazyWithRetry(() => import('./pages/public/TermosServico'))
const knownRestrictedRoutes = ['/hoje','/rotinas','/gestao','/fechamento','/financeiro','/clientes','/senhas','/pessoas','/usuarios','/auditoria','/saude','/configuracoes','/agenda','/crm','/produtos','/atendimento','/diretoria-ia']

function PageFallback() {
  return (
    <div className="app-loading-screen" role="status" aria-live="polite" aria-label="Preparando o sistema">
      <div className="app-loading-brand" aria-hidden="true"><span>A</span><i/><span>T</span></div>
      <div className="app-loading-copy"><strong>Aden Technology</strong><span className="spell-shimmer-text">Preparando seu ambiente</span></div>
      <div className="app-loading-progress" aria-hidden="true"><i/><i/><i/></div>
    </div>
  )
}

function AppRoutes() {
  const { user, features, loading, hasModule } = useAuth()
  const location = useLocation()
  // Alterar paginação ou filtros mantém a mesma tela e não deve cancelar a
  // consulta que acabou de ser iniciada por essa própria atualização da URL.
  useLayoutEffect(()=>beginRequestScope(location.pathname),[location.pathname])
  if (loading) return <PageFallback/>
  if (!user) return (
    <Suspense fallback={<PageFallback/>}>
      <Routes>
        <Route path="/login" element={<Login/>}/>
        <Route path="/redefinir-senha" element={<Login/>}/>
        <Route path="/orcamento/:token" element={<OrcamentoPublico/>}/>
        <Route path="/aceitar-convite/:token" element={<AceitarConvite/>}/>
        <Route path="/privacidade" element={<PoliticaPrivacidade/>}/>
        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade/>}/>
        <Route path="/privacy" element={<PoliticaPrivacidade/>}/>
        <Route path="/termos" element={<TermosServico/>}/>
        <Route path="/termos-de-servico" element={<TermosServico/>}/>
        <Route path="/terms" element={<TermosServico/>}/>
        <Route path="/chatwoot/dashboard-app" element={<ChatwootDashboardApp/>}/>
        <Route path="/chatwoot-widget/ticket" element={<ChatwootDashboardApp/>}/>
        <Route path="*" element={<Navigate to="/login" replace/>}/>
      </Routes>
    </Suspense>
  )
  const isAdmin=user.role==='admin', isFinanceiro=user.role==='financeiro', isTecnico=user.role==='tecnico', isCliente=user.role==='cliente'
  const isClienteAdmin = isCliente && (user.role_cliente||'comum')==='administrador'
  const restricted = knownRestrictedRoutes.some(route => location.pathname === route || location.pathname.startsWith(`${route}/`))
  const LA=LayoutAdmin, LC=LayoutCliente
  const D=(isAdmin||isFinanceiro) ? DashboardAdmin : isTecnico ? DashboardTecnico : DashboardCliente
  const ML=isCliente ? LC : LA
  if (user.must_change_password) {
    return (
      <Suspense fallback={<PageFallback/>}>
        <Routes>
          <Route path="/alterar-senha-obrigatoria" element={<AlteracaoSenhaObrigatoria/>}/>
          <Route path="*" element={<Navigate to="/alterar-senha-obrigatoria" replace state={{ from: location.pathname }}/>}/>
        </Routes>
      </Suspense>
    )
  }
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace/>}/>
      <Route path="/alterar-senha-obrigatoria" element={<Navigate to="/" replace/>}/>

      {/* Casca Persistente: LayoutAdmin/LayoutCliente montado uma única vez, sem piscar a tela */}
      <Route element={<ML><Outlet /></ML>}>
        <Route path="/hoje" element={isCliente ? <Navigate to="/" replace/> : hasModule('hoje') ? <Hoje/> : <RouteState forbidden/>}/>
        <Route path="/dashboard" element={hasModule('dashboard') ? <D/> : <Navigate to="/" replace/>}/>
        <Route path="/" element={hasModule('dashboard') ? <D/> : <Navigate to={hasModule('chamados') ? '/chamados' : '/perfil'} replace/>}/>
        <Route path="/chamados" element={hasModule('chamados') ? <Chamados/> : <RouteState forbidden/>}/>
        <Route path="/chamados/:id" element={hasModule('chamados') ? <DetalheChamado/> : <RouteState forbidden/>}/>
        {!isCliente && <>
          <Route path="/atendimento" element={<AtendimentoWhatsApp initialTab="chat"/>}/>
          <Route path="/atendimento/chat" element={<Navigate to="/atendimento" replace/>}/>
          <Route path="/atendimento/conexao" element={<AtendimentoWhatsApp initialTab="conexao"/>}/>
          <Route path="/atendimento/agendamentos" element={<AtendimentoWhatsApp initialTab="agendamentos"/>}/>
          <Route path="/atendimento/campanhas" element={<AtendimentoWhatsApp initialTab="campanhas"/>}/>
          <Route path="/atendimento/auto-replies" element={<AtendimentoWhatsApp initialTab="autoreplies"/>}/>
          <Route path="/atendimento/seguranca" element={<AtendimentoWhatsApp initialTab="accesscontrol"/>}/>
          <Route path="/atendimento/regua" element={<AtendimentoWhatsApp initialTab="regua"/>}/>
          <Route path="/atendimento/configuracoes" element={<AtendimentoWhatsApp initialTab="config"/>}/>
          {hasModule('rotinas') && <Route path="/rotinas" element={<RotinasPreventivas/>}/>}
          {hasModule('gestao') && <><Route path="/ativos" element={<Ativos/>}/><Route path="/conhecimento" element={<Conhecimento/>}/></>}
          <Route path="/diretoria-ia" element={<DiretoriaIA/>}/>
          {hasModule('senhas') && <Route path="/senhas" element={<BancoSenhas/>}/>}
        </>}
        <Route path="/perfil" element={<Perfil/>}/>
        {isCliente && <Route path="/ajuda" element={<CentralAjuda/>}/>}
        
        {!isCliente && hasModule('financeiro') && <Route path="/fechamento" element={<Navigate to="/financeiro" replace/>}/>}
        {isClienteAdmin && <Route path="/fechamento" element={<Fechamento/>}/>}
        {((!isCliente && hasModule('financeiro'))||isClienteAdmin) && <>
          <Route path="/fechamento/:id" element={<FaturaDetalhe/>}/>
      </>}
      {!isCliente && <>
        {hasModule('clientes') && <><Route path="/clientes" element={<Clientes/>}/><Route path="/clientes/:id" element={<DetalheCliente/>}/><Route path="/contratos/:id" element={<DetalheContrato/>}/></>}
        {hasModule('usuarios') && <Route path="/usuarios" element={<Usuarios/>}/>}
        {hasModule('auditoria') && <Route path="/auditoria" element={<Auditoria/>}/>}
        {hasModule('saude') && <Route path="/saude" element={<SaudeOperacional/>}/>}
        {hasModule('configuracoes') && <Route path="/configuracoes" element={<Configuracoes/>}/>}
        {hasModule('financeiro') && <>
          <Route path="/financeiro" element={<Financeiro section="resumo"/>}/>
          <Route path="/financeiro/transacoes" element={<Financeiro section="resumo"/>}/>
          <Route path="/financeiro/pagar" element={<Financeiro section="pagar"/>}/>
          <Route path="/financeiro/faturas" element={<Financeiro section="faturas"/>}/>
          <Route path="/financeiro/contas" element={<Financeiro section="contas"/>}/>
          <Route path="/financeiro/tesouraria" element={<Financeiro section="contas"/>}/>
          <Route path="/financeiro/conciliacao" element={<Financeiro section="conciliacao"/>}/>
          <Route path="/financeiro/fluxo" element={<Financeiro section="fluxo"/>}/>
          <Route path="/financeiro/dre" element={<Financeiro section="dre"/>}/>
          <Route path="/financeiro/plano-contas" element={<Financeiro section="plano"/>}/>
          <Route path="/financeiro/plano" element={<Financeiro section="plano"/>}/>
          <Route path="/financeiro/operacao" element={<Financeiro section="operacao"/>}/>
          <Route path="/financeiro/capacidade" element={<Financeiro section="capacidade"/>}/>
          <Route path="/capacidade" element={<Financeiro section="capacidade"/>}/>
          <Route path="/financeiro/precificacao" element={<Financeiro section="precificacao"/>}/>
        </>}
        {hasModule('agenda') && <Route path="/agenda" element={<Agenda/>}/>}
      </>}
      {!isCliente && hasModule('crm') && features.crm?.enabled && (
        <>
          <Route path="/crm" element={<CrmBoard/>}/>
          <Route path="/crm/analytics" element={<CrmAnalytics/>}/>
          <Route path="/crm/atendimento" element={<Navigate to="/atendimento" replace/>}/>
          <Route path="/crm/campanhas" element={<Navigate to="/atendimento/campanhas" replace/>}/>
          <Route path="/crm/agendamentos" element={<Navigate to="/atendimento/agendamentos" replace/>}/>
          <Route path="/crm/auto-replies" element={<Navigate to="/atendimento/auto-replies" replace/>}/>
          <Route path="/crm/seguranca" element={<Navigate to="/atendimento/seguranca" replace/>}/>
          <Route path="/crm/configuracoes-whatsapp" element={<Navigate to="/atendimento/configuracoes" replace/>}/>
          {(isAdmin || isFinanceiro) && <Route path="/crm/produtividade" element={<CrmProdutividade/>}/>}
          {(isAdmin || isFinanceiro) && <Route path="/crm/configuracoes" element={<CrmConfiguracoes/>}/>}
          <Route path="/crm/oportunidade/:id" element={<CrmDetalhe/>}/>
        </>
      )}
      {!isCliente && hasModule('produtos_estoque') && <>
        <Route path="/produtos" element={<CatalogoPrecificacao/>}/>
        <Route path="/produtos/planos" element={<CatalogoPlanos/>}/>
        <Route path="/produtos/modulos" element={<CatalogoModulos/>}/>
        <Route path="/produtos/estoque" element={<EstoqueOperacao section="estoque"/>}/>
        <Route path="/produtos/compras" element={<EstoqueOperacao section="compras"/>}/>
        <Route path="/produtos/reservas" element={<EstoqueReservas/>}/>
        <Route path="/produtos/inventarios" element={<EstoqueInventarios/>}/>
        <Route path="/crm/catalogo" element={<Navigate to="/produtos" replace/>}/>
        <Route path="/crm/catalogo/planos" element={<Navigate to="/produtos/planos" replace/>}/>
        <Route path="/crm/catalogo/modulos" element={<Navigate to="/produtos/modulos" replace/>}/>
      </>}
      {!isCliente && hasModule('pessoas') && (user.role !== 'tecnico' || user.bate_ponto) && <>
        <Route path="/pessoas" element={<Navigate to={user.role === 'tecnico' ? '/pessoas/ponto' : '/pessoas/equipe'} replace/>}/>
        <Route path="/pessoas/ponto" element={<Pessoas/>}/>
        <Route path="/pessoas/folha" element={<Pessoas/>}/>
        <Route path="/pessoas/equipe" element={<Pessoas/>}/>
        <Route path="/pessoas/ferias" element={<Pessoas/>}/>
        <Route path="/pessoas/folha-pagamento" element={<Pessoas/>}/>
        <Route path="/pessoas/rescisoes" element={<Pessoas/>}/>
        {user.role === 'admin' && <Route path="/pessoas/gestao-ponto" element={<GestaoPonto/>}/>}
        <Route path="/pessoas/acertos" element={<Pessoas/>}/>
        <Route path="/pessoas/configuracao" element={<Pessoas/>}/>
      </>}
      <Route path="/orcamento/:token" element={<OrcamentoPublico/>}/>
      <Route path="/aceitar-convite/:token" element={<AceitarConvite/>}/>
      <Route path="/privacidade" element={<PoliticaPrivacidade/>}/>
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade/>}/>
      <Route path="/privacy" element={<PoliticaPrivacidade/>}/>
      <Route path="/termos" element={<TermosServico/>}/>
      <Route path="/termos-de-servico" element={<TermosServico/>}/>
      <Route path="/terms" element={<TermosServico/>}/>
      <Route path="/chatwoot/dashboard-app" element={<ChatwootDashboardApp/>}/>
      <Route path="/chatwoot-widget/ticket" element={<ChatwootDashboardApp/>}/>
        <Route path="*" element={<RouteState forbidden={restricted}/>}/>
      </Route>
    </Routes>
  )
}

function AppContent() {
  const location = useLocation()
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <AppRoutes/>
    </ErrorBoundary>
  )
}

function useCurrentTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof document === 'undefined') return 'dark'
    return document.documentElement.dataset.theme || localStorage.getItem('theme') || 'dark'
  })

  useEffect(() => {
    const root = document.documentElement
    const update = () => setTheme(root.dataset.theme || localStorage.getItem('theme') || 'dark')
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] })
    window.addEventListener('storage', update)
    return () => {
      observer.disconnect()
      window.removeEventListener('storage', update)
    }
  }, [])

  return theme
}

export default function App() {
  const currentTheme = useCurrentTheme()

  return (
    <BrowserRouter>
      <AuthProvider>
        <a href="#conteudo-principal" className="skip-link">Ir para o conteúdo principal</a>
        <Toaster
          position="top-center"
          theme={currentTheme}
          closeButton
          duration={3500}
          offset="68px"
        />
        <AppContent/>
      </AuthProvider>
    </BrowserRouter>
  )
}
